import React, { useRef, useEffect } from 'react';
import {
  Box,
  Text,
  TextInput,
  Stack,
  ScrollArea,
  Group,
  Paper,
  ActionIcon,
  Alert,
} from '@mantine/core';
import { IconSend, IconAlertCircle, IconRefresh } from '@tabler/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PRIMARY_OPACITY_80 } from '../../constants/colors';
import { useAccountSetupChat } from '../../hooks/useAccountSetupChat';

interface ChatComponentProps {
  className?: string;
  style?: React.CSSProperties;
  onTokenUsageChange?: (totalTokens: number) => void;
}

export const ChatComponent: React.FC<ChatComponentProps> = ({ 
  className, 
  style, 
  onTokenUsageChange 
}) => {
  const {
    messages,
    sendMessage,
    isLoading,
    error,
    clearChat,
    totalTokensUsed,
  } = useAccountSetupChat();
  
  const [inputValue, setInputValue] = React.useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Notify parent component of token usage changes
  useEffect(() => {
    if (onTokenUsageChange) {
      onTokenUsageChange(totalTokensUsed);
    }
  }, [totalTokensUsed, onTokenUsageChange]);

  // Log component mount and error state changes
  useEffect(() => {
    return () => console.log('🎬 [Chat Component] Component unmounted');
  }, []);

  useEffect(() => {
    if (error) {
      console.error('🚨 [Chat Component] Error state changed:', error);
    }
  }, [error]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) {
      console.warn('🚫 [Chat Component] Cannot send - empty message or loading');
      return;
    }
    
    const message = inputValue.trim();
   
    setInputValue('');
    
    try {
      await sendMessage(message);
      
    } catch (error) {
      console.error('❌ [Chat Component] Failed to send message:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      className={className}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >

      {/* Messages Area */}
      <ScrollArea
        ref={scrollAreaRef}
        style={{ flex: 1 }}
        p="md"
        sx={{ height: 0 }} // This ensures the ScrollArea takes available space
      >
        <Stack spacing="md">
          {/* Error Alert */}
          {error && (
            <Alert 
              icon={<IconAlertCircle size={16} />} 
              color="red" 
              variant="light"
              withCloseButton
              onClose={() => clearChat()}
            >
              <Group position="apart" align="center">
                <Text size="sm" style={{ flex: 1 }}>{error}</Text>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => window.location.reload()}
                  title="Refresh page"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Group>
            </Alert>
          )}
          {messages.map((message) => (
            <Group
              key={message.id}
              align="flex-start"
              spacing="sm"
              className={message.sender === 'user' ? 'user-message' : 'agent-message'}
              sx={{
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              
              <Paper
                p="sm"
                sx={(theme) => ({
                  maxWidth: '80%',
                  backgroundColor: message.sender === 'user' 
                    ? PRIMARY_OPACITY_80 // Darker purple for better contrast
                    : 'transparent', // No background for agent messages
                  color: message.sender === 'user' ? 'white' : 'inherit',
                })}
              >
                <Box 
                  className={`chat-markdown ${message.sender === 'user' ? 'user-message' : 'agent-message'}`}
                  sx={{
                    fontSize: '14px', // Matches Mantine's Text size="sm"
                    lineHeight: 1.5,
                    // Clean slate - all styling handled by ReactMarkdown components
                  }}
                >
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Remove default paragraph margins for better control
                      p: ({children}) => <div style={{ marginBottom: '0.5rem', lineHeight: '1.5' }}>{children}</div>,
                      // Headers with proper styling
                      h1: ({children}) => <h1 style={{ fontSize: '1.4em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h1>,
                      h2: ({children}) => <h2 style={{ fontSize: '1.3em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h2>,
                      h3: ({children}) => <h3 style={{ fontSize: '1.2em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h3>,
                      h4: ({children}) => <h4 style={{ fontSize: '1.1em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h4>,
                      h5: ({children}) => <h5 style={{ fontSize: '1em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h5>,
                      h6: ({children}) => <h6 style={{ fontSize: '0.9em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' }}>{children}</h6>,
                      // Lists with proper spacing
                      ul: ({children}) => <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ul>,
                      ol: ({children}) => <ol style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>{children}</ol>,
                      li: ({children}) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
                      // Code blocks with background
                      pre: ({children}) => (
                        <pre style={{ 
                          backgroundColor: message.sender === 'user' ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          overflow: 'auto',
                          fontSize: '13px',
                          margin: '0.5rem 0',
                          whiteSpace: 'pre-wrap', 
                          wordWrap: 'break-word' 
                        }}>
                          {children}
                        </pre>
                      ),
                      // Inline code with background
                      code: ({children, ...props}: any) => {
                        // Check if it's inline code (not in a pre block)
                        const isInline = !props.className?.includes('language-');
                        if (isInline) {
                          return (
                            <code 
                              style={{ 
                                backgroundColor: message.sender === 'user' ? 'rgba(255,255,255,0.15)' : '#f0f0f0',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '13px',
                                fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }
                        return <code {...props}>{children}</code>;
                      },
                      // Blockquotes
                      blockquote: ({children}) => (
                        <blockquote style={{
                          borderLeft: '3px solid',
                          borderLeftColor: message.sender === 'user' ? 'rgba(255,255,255,0.3)' : '#ddd',
                          paddingLeft: '1rem',
                          margin: '0.5rem 0',
                          fontStyle: 'italic'
                        }}>
                          {children}
                        </blockquote>
                      ),
                      // Links
                      a: ({children, href}) => (
                        <a 
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: message.sender === 'user' ? '#B3D9FF' : '#4A90E2',
                            textDecoration: 'underline'
                          }}
                        >
                          {children}
                        </a>
                      ),
                      // Strong/Bold
                      strong: ({children}) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                      // Emphasis/Italic
                      em: ({children}) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                      // Ensure proper line breaks
                      br: () => <br />
                    }}
                  >
                    {message.text}
                  </ReactMarkdown>
                </Box>
                <Text size="xs" color={message.sender === 'user' ? 'gray.3' : 'dimmed'} mt={4}>
                  {formatTime(message.timestamp)}
                </Text>
              </Paper>

            </Group>
          ))}
          
          {isLoading && (
            <Group align="flex-start" spacing="sm" className="agent-message">
              <Paper p="sm" sx={{ backgroundColor: 'transparent' }}>
                <Text size="sm" color="dimmed" className="typing-indicator">Agent is typing...</Text>
              </Paper>
            </Group>
          )}
        </Stack>
      </ScrollArea>

      {/* Input Area */}
      <Paper
        p="xs"
      >
        <Group spacing="sm" align="center">
          <TextInput
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            style={{ flex: 1 }}
            autoComplete="off"
          />
          <ActionIcon
            size="lg"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            loading={isLoading}
            sx={{
              height: '36px', // Slightly smaller but still aligned
              width: '36px',
              backgroundColor: '#5B60E3',
              color: 'white',
              '&:hover': {
                backgroundColor: '#4C52D4',
              },
              '&:disabled': {
                backgroundColor: '#9A9CE8',
                opacity: 0.6,
              },
            }}
          >
            <IconSend size={16} />
          </ActionIcon>
        </Group>
      </Paper>
    </Box>
  );
};

export default ChatComponent;
